/**
 * useGestureNotice — un geste refusé d'un écran du design system, dit par l'écran lui-même : un message
 * (`notice`) que sa page pose dans son `<Snackbar>`, avec le message du serveur, sinon `fallback`.
 *
 * Sous l'espace tuteur, les boîtes de l'ancien kit se taisent (`useOwnNetworkFailuresWhileFocused`, et
 * les écouteurs de la coquille legacy) : un geste dont seule une de ces boîtes disait l'échec (un
 * « Rafraîchir », un lien Stripe) le dit ici.
 *
 * `readOptions()` : les options d'`apiRequest` d'UN geste qui lance plusieurs lectures (« Rafraîchir »).
 * Le premier refus se dit, une fois ; un geste plus récent rend les réponses de l'ancien muettes (le
 * message d'un rafraîchissement dépassé n'efface pas celui d'aujourd'hui). Un 401 (la déconnexion) et
 * un 419 (rejoué par `apiRequest`) ne se disent pas : ce n'est pas un refus du geste.
 */
import { useCallback, useRef, useState } from "react"

import { apiErrorMessage } from "./apiErrorMessage"

type ReadResult = { ok: boolean; error?: unknown }

/** Ce qu'`apiRequest` ne présente pas comme un refus du geste : il déconnecte, ou rejoue. */
const NOT_A_REFUSAL = new Set([401, 419])

export function useGestureNotice(fallback: string) {
    const [notice, setNotice] = useState<string | null>(null)
    const gestureRef = useRef(0)

    const tell = useCallback((error: unknown) => setNotice(apiErrorMessage(error, fallback)), [fallback])
    const dismiss = useCallback(() => setNotice(null), [])

    const readOptions = useCallback(() => {
        const gesture = ++gestureRef.current
        setNotice(null)
        let told = false
        return {
            offlineDialog: true,
            onResult: (result: ReadResult) => {
                if (result.ok || told || gesture !== gestureRef.current) return
                const status = (result.error as { status?: unknown } | undefined)?.status
                if (typeof status === "number" && NOT_A_REFUSAL.has(status)) return
                told = true
                tell(result.error)
            },
        }
    }, [tell])

    return { notice, tell, dismiss, readOptions }
}
